import { Fragment } from 'react';
import ChurchIcon from '@mui/icons-material/Church';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import { Box, Typography } from '@mui/material';
import { IconText, type IconTextProps } from '@core/components';
import type { Order } from '../../../api/order';
import OrderActionsBar from '../../components/OrderActionsBar';
import OrderDueDateChip from '../../components/OrderDueDateChip';
import OrderStatusChip from '../../components/OrderStatusChip';
import { getFullName } from '../../utils/customer';

interface OrderDetailHeaderProps {
  order: Order;
  onUpdated: () => void;
  onDeleted: () => void;
}

const OrderDetailHeader = ({
  order,
  onUpdated,
  onDeleted,
}: OrderDetailHeaderProps) => {
  const { customer, notes } = order;
  const diocese = customer.diocese?.name?.trim();
  const address = customer.address?.trim();
  const email = customer.email?.trim();
  const phone = customer.phone?.trim();

  const segments = [
    diocese && {
      key: 'diocese',
      title: 'Diocese',
      icon: <ChurchIcon fontSize="small" />,
      text: diocese,
    },
    address && {
      key: 'address',
      title: 'Address',
      icon: <LocationOnIcon fontSize="small" />,
      text: address,
    },
    email && {
      key: 'email',
      title: 'Email',
      icon: <EmailIcon fontSize="small" />,
      text: email,
      href: `mailto:${email}`,
    },
    phone && {
      key: 'phone',
      title: 'Phone',
      icon: <PhoneIcon fontSize="small" />,
      text: phone,
      href: `tel:${phone}`,
    },
  ].filter(Boolean) as ({ key: string } & IconTextProps)[];

  return (
    <Box
      sx={{
        gap: 1,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
        gridTemplateAreas: {
          xs: `
            "chips"
            "name"
            "contact"
            "notes"
            "actions"
          `,
          md: `
            "title actions"
            "contact contact"
            "notes notes"
          `,
        },
      }}
    >
      <Box
        sx={{
          gridArea: 'title',
          display: { xs: 'contents', md: 'flex' },
          flexWrap: { md: 'wrap' },
          alignItems: { md: 'flex-end' },
          gap: { md: 1 },
          minWidth: 0,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{
            gridArea: { xs: 'name', md: 'unset' },
            minWidth: 0,
            lineHeight: 1.5,
            whiteSpace: { md: 'nowrap' },
            textTransform: 'uppercase',
          }}
        >
          {getFullName(customer)}
        </Typography>
        <Box
          sx={{
            gridArea: { xs: 'chips', md: 'unset' },
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <OrderStatusChip order={order} />
          <OrderDueDateChip dueDate={order.dueDate} status={order.status} />
        </Box>
      </Box>

      {segments.length > 0 ? (
        <Box
          sx={{
            gridArea: 'contact',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            flexWrap: { md: 'wrap' },
            alignItems: { md: 'center' },
            gap: 1,
          }}
        >
          {segments.map(({ key, ...props }, index) => (
            <Fragment key={key}>
              {index > 0 ? (
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: { xs: 'none', md: 'inline' } }}
                >
                  •
                </Typography>
              ) : null}
              <IconText {...props} />
            </Fragment>
          ))}
        </Box>
      ) : null}

      {notes ? (
        <Typography
          variant="body2"
          color="text.secondary"
          fontStyle="italic"
          sx={{ gridArea: 'notes' }}
        >
          {notes}
        </Typography>
      ) : null}

      <Box
        sx={{
          gridArea: 'actions',
          alignSelf: { md: 'flex-end' },
          justifySelf: { md: 'end' },
        }}
      >
        <OrderActionsBar
          order={order}
          onUpdated={onUpdated}
          onDeleted={onDeleted}
        />
      </Box>
    </Box>
  );
};

export default OrderDetailHeader;
